// Due status — the one place that says what "overdue" / "due soon" / "upcoming" mean and what they
// look like. Shared by the Ongoing list, the Story header and the Global timeline so a status can
// never drift between two screens (it used to be copy-pasted in both components).

import { WAITING_META } from './waitingStatus'

// Labels + colours exactly as the Ongoing list and Story header have always rendered them.
const STATUS_META = {
  // The top of the ongoing ladder (busy-timeline-srv#27, spec §12): the user owes an answer — an
  // unconfirmed suggestion, or a wait that has run past its date. It wears the SAME alarm red as
  // overdue, because it asks for the same thing: do something about this now.
  needsAttention: { label: 'Needs attention', color: '#ef4444' },
  overdue: { label: 'Overdue', color: '#ef4444' },
  // An event under way. Green — this is not a warning, it is where the user is supposed to be, and
  // it is the one status that resolves by itself.
  happeningNow: { label: 'Happening now', color: '#22c55e' },
  dueSoon: { label: 'Due soon', color: '#ff8800' },
  // Not yet urgent — the muted grey the panels use for anything that needs no attention right now.
  upcoming: { label: 'Upcoming', color: '#9ca3af' },
  // Waiting is no longer a rung of THIS ladder (busy-timeline-srv#26): the next move belongs to
  // somebody else, so its rungs, window and words live in lib/waitingStatus and this entry is that
  // file's chip. Same label and colour as it has always been — the difference is that there is now
  // one definition of them, and a row that knows its expected date gets the fuller ladder there.
  waiting: WAITING_META.waiting,
  // News rather than work: something moved in the last day and nothing is due. Grey-blue — the
  // muted family the "no attention needed" states already use, tinted toward the waiting blue so it
  // reads as an event in the story rather than as a dormant row.
  updated: { label: 'Updated', color: '#64748b' },
  completed: { label: 'Completed', color: '#22c55e' },
  archived: { label: 'Archived', color: '#9ca3af' },
  // The two states only an EVENT can be in (busy-my-web#238, Saffet: "after the event has passed we
  // should not show an event as overdue as it is an event … after it passes it has passed").
  // `now` is the event while it is happening — worth saying, so it wears the due-soon amber the
  // hour before it already wore. `past` is the event afterwards, and it is NEWS ABOUT NOTHING: the
  // muted grey every other "needs no attention" state uses, never the overdue red.
  now: { label: 'Now', color: '#ff8800' },
  past: { label: 'Past', color: '#9ca3af' },
  // A REMINDER that has fired (busy-my-web#410). Not a rung of the due ladder — nothing is late
  // because a reminder went off, it is simply a thing that HAPPENED — so it wears the design
  // sheet's own `state/reminder` amber rather than borrowing overdue's red or upcoming's grey.
  // The Global timeline derives a "Reminder due" row from it; see lib/globalTimelineFeed.
  reminder: { label: 'Reminder', color: '#eab308' }
}

const FALLBACK = { label: 'Active', color: '#22c55e' }

// The Global timeline frame (40006440:182480) states its statuses in the Figma DESIGN TOKENS rather
// than in the softer palette the Ongoing list and the Story header use: pure red for overdue and the
// navy "upcoming" of the token sheet, not the muted grey those two picked for "needs no attention".
// Both palettes therefore live here, in the one file that owns what a status looks like — a second
// copy inside the panel is exactly the drift this module exists to prevent.
const STATUS_TOKEN_COLOR = {
  // The ladder's two new states in the token sheet's own palette: "needs attention"
  // borrows `state/overdue` (it is the same demand on the user), "happening now" the green the
  // sheet gives a live state.
  needsAttention: '#FF0000',
  happeningNow: '#00BD62',
  overdue: '#FF0000',
  dueSoon: '#FF8800',
  upcoming: '#2D3166',
  waiting: '#3741F3',
  updated: '#64748B',
  completed: '#00BD62',
  archived: '#9CA3AF',
  // An event in progress borrows `color state/due soon`; a PAST event is given neither of the two
  // alarm tokens the design sheet names (`state/reminder` #EAB308, `state/overdue` #FF0000) — it
  // reads in the neutral grey (busy-my-web#238).
  now: '#FF8800',
  past: '#9CA3AF',
  // `state/reminder` — the one alarm token the sheet names besides `state/overdue` (see above), and
  // the colour a fired reminder reads in on the Global timeline (busy-my-web#410).
  reminder: '#EAB308',
}

// The status colour as the Figma token sheet writes it (the Global timeline's status line). Falls
// back to the shared chip colour for anything the token sheet does not name.
export function statusTokenColor(status) {
  return STATUS_TOKEN_COLOR[status] || statusMeta(status).color
}

// The compact unit(s) `dueAmount` produces ("1h", "45m", "3d", or the overdue "3d 8h"), spelled out
// in full with the correct plural. `dueAmount` is one unit except for overdue amounts over a day,
// which carry a second, smaller unit (see its "one unit, with one exception" comment) — so this
// spells out one or two space-separated `<n><unit>` tokens, not a general pluralizer.
const UNIT_WORD = { m: 'minute', h: 'hour', d: 'day' }
function spellOutAmount(amt) {
  const m = /^(\d+)([mhd])(?: (\d+)([mhd]))?$/.exec(amt)
  if (!m) return amt
  const [, n1, unit1, n2, unit2] = m
  const first = `${n1} ${UNIT_WORD[unit1]}${n1 === '1' ? '' : 's'}`
  if (!n2) return first
  return `${first} ${n2} ${UNIT_WORD[unit2]}${n2 === '1' ? '' : 's'}`
}

// The status IN THE FRAME'S OWN WORDS, for the Global timeline's status detail line:
//   "Overdue 1 hour"  ·  "Overdue 3 days 8 hours"  ·  "Due soon by 1 hour"  ·  "Coming up 12 hours"
//   ·  "Waiting on a reply"
// The frame (40007749:343432) only evidences "Overdue 1 hour", but the compact-to-word spelling is
// applied to every status that embeds an amount so the line stays internally consistent rather than
// switching notation status to status. Built from the amount `dueAmount` already produced, so the
// line and the tooltip can never disagree on the underlying number — only on how it's spelled (that
// includes the overdue two-unit exception, busy-my-web#140 — `dueAmount` already emits "3d 8h ago",
// `spellOutAmount` just spells whatever it's handed).
// With no amount (an all-day row) it falls back to the plain label — there is no "by how much".
export function statusText(status, amount = '') {
  const { label } = statusMeta(status)
  const amt = spellOutAmount(String(amount || '').trim().replace(/^in /, '').replace(/ ago$/, ''))
  if (status === 'waiting') return 'Waiting on a reply'
  // An event's two states say themselves in full (lib/eventTime.js writes "Ended 38m ago" / "Now"),
  // so the amount is never spelled out after them — "Past 3 hours" would read as a lateness.
  if (status === 'past' || status === 'now') return label
  if (!amt || amt === 'now') return status === 'overdue' && amt === 'now' ? 'Overdue now' : label
  if (status === 'overdue') return `Overdue ${amt}`
  if (status === 'dueSoon') return `Due soon by ${amt}`
  if (status === 'upcoming') return `Coming up ${amt}`
  return `${label} ${amt}`
}

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

// "Due soon" is the next hour — deliberately tighter than the follow-ups panel's week.
export const DUE_SOON_MS = HOUR_MS

// Grouped display order: what's late first, then what's about to be late.
export const STATUS_ORDER = ['overdue', 'dueSoon', 'upcoming']

// The chip (label + colour) for a status; anything unknown reads as a plain active item.
export function statusMeta(status) {
  return STATUS_META[status] || FALLBACK
}

// THE ONGOING LIST'S OWN PALETTE (2026-09-15 parity audit A-7, busy-my-web#458; frames
// 40001396:89743 and 40007749:343432). The sidebar list — and the collapsed rail's Ongoing popover,
// which is the same list — states two rungs differently from the Story header and the Global
// timeline: a story with nothing due reads "active" (lower-case, in the design's purple, not the
// green "Active" `FALLBACK` the header keeps) and "Due soon" wears the secondary grey rather than
// the amber. Only the LIST reads through this; `statusMeta` above is untouched for every other
// surface, so the header and the timeline keep the palette their own frames were measured from.
// The remaining rungs are the shared ones — a second full copy would be the drift this file exists
// to prevent.
const LIST_STATUS_META = {
  dueSoon: { label: 'Due soon', color: '#4b5563' },
}
const LIST_FALLBACK = { label: 'active', color: '#8a38f5' }

export function listStatusMeta(status) {
  return LIST_STATUS_META[status] || STATUS_META[status] || LIST_FALLBACK
}

// Backend timestamps are UTC but often serialized WITHOUT a zone suffix — parsing those as local
// shifts every time by the user's offset (the "everything says 3h ago" bug). Treat naive as UTC.
export function parseUtc(d) {
  if (typeof d === 'string' && d && !/[zZ]$|[+-]\d{2}:?\d{2}$/.test(d)) return new Date(`${d}Z`)
  return new Date(d)
}

// HOW LATE, or how long is left: "2h ago" past the due date, "in 45m" before it, "now" within the
// minute either side. The status says which side of the line a row is on; this says by how much, so
// "Overdue · 2h ago" reads at a glance. One unit, the biggest that fits (minutes under an hour, hours
// under a day, then days) — because it sits in a 10px line next to the status — WITH one deliberate
// exception (busy-my-web#140): a PAST-due amount of a day or more also carries the leftover hours
// ("3d 8h ago"), because "Overdue 3 days" hides up to 23 hours of lateness and Saffet asked for the
// same two-unit precision the due-soon amount already has. Due soon / upcoming stay one unit — that
// revision is still open. A whole number of days with no remainder drops the second unit ("3d ago").
// Empty string for anything undated or unparseable, so a caller can drop it without a check.
export function dueAmount(due, now = Date.now()) {
  if (due === null || due === undefined || due === '') return ''
  const dt = due instanceof Date ? due : parseUtc(due)
  const ms = dt?.getTime?.()
  if (!ms && ms !== 0) return ''
  if (isNaN(ms)) return ''
  const diff = ms - now
  const abs = Math.abs(diff)
  if (abs < MINUTE_MS) return 'now'
  const amount = abs < HOUR_MS
    ? `${Math.floor(abs / MINUTE_MS)}m`
    : abs < DAY_MS
      ? `${Math.floor(abs / HOUR_MS)}h`
      : diff < 0
        ? overdueDaysAmount(abs)
        : `${Math.floor(abs / DAY_MS)}d`
  return diff < 0 ? `${amount} ago` : `in ${amount}`
}

// The "3d 8h" / "3d" half of the overdue two-unit exception above — split out so `dueAmount` reads
// as one unit-selection ladder with a single named branch for the exception, not an inline dogleg.
function overdueDaysAmount(abs) {
  const days = Math.floor(abs / DAY_MS)
  const hours = Math.floor((abs % DAY_MS) / HOUR_MS)
  return hours ? `${days}d ${hours}h` : `${days}d`
}

// The status IN WORDS, for the bell's tooltip / aria-label on a Global timeline row: the colour of
// the bell is the whole status on screen, and a colour alone is not readable — this is the sentence
// that says the same thing. Short by design (one line in a 21em tooltip) and built from the amount
// `dueAmount` already produced, so the tooltip and the visible amount can never disagree.
//   overdue + "2h ago" -> "Overdue by 2h"      dueSoon/upcoming + "in 45m" -> "Due in 45m"
//   overdue + "3d 8h ago" -> "Overdue by 3d 8h"   (the overdue two-unit exception, busy-my-web#140 —
//   stays in `dueAmount`'s compact notation here, same as the single-unit case)
//   waiting + "in 2d"  -> "Waiting on a reply · due in 2d"
// With no amount at all (an all-day event) it falls back to the plain label, because there is no
// "by how much" to state.
export function statusExplanation(status, amount = '') {
  const { label } = statusMeta(status)
  const amt = String(amount || '').trim()
  if (!amt) return status === 'waiting' ? 'Waiting on a reply' : label
  if (status === 'overdue') return amt === 'now' ? 'Overdue now' : `Overdue by ${amt.replace(/ ago$/, '')}`
  if (status === 'waiting') return `Waiting on a reply · due ${amt}`
  return amt === 'now' ? 'Due now' : `Due ${amt}`
}

// NO DUE WINDOW WITHOUT A TIME SOMEBODY CHOSE (busy-my-web#242).
//
// Fatemeh: "من تایمی برای ایونت انتخاب نکردم اما زده due soon" — she created an event without
// picking a time and the story called it "Due soon". Nothing was wrong with the arithmetic above: a
// calendar event MUST have a start, so the box invents one (the next full hour) to have something
// to send — and an instant inside the next hour is, by this file's own definition, due soon. The
// fault is that an invented start travelled on as a due date at all.
//
// So the rule is stated here, beside the statuses it feeds: a due window is only ever about a
// moment the USER chose. Undefined otherwise, which is precisely what every reader of a due date
// already treats as "undated" — `dueStatus` returns null, `dueAmount` returns '' and the story step
// carries no due line.
//
// @param picked did the user actually set a time?
// @param iso    the instant that WOULD be sent (the invented one included)
export function chosenDue(picked, iso) {
  return picked ? (iso || undefined) : undefined
}

// overdue (past) / dueSoon (within the hour) / upcoming (later). `null` when there's no usable date —
// an undated item has nothing to be late for, and callers drop it rather than guess.
export function dueStatus(due, now = Date.now()) {
  // `new Date(null)` is the unix EPOCH, not an invalid date — without this guard a row with no due
  // date would come back "overdue" (dated 1 Jan 1970) instead of being dropped.
  if (due === null || due === undefined || due === '') return null
  const dt = due instanceof Date ? due : parseUtc(due)
  const ms = dt?.getTime?.()
  if (!ms && ms !== 0) return null
  if (isNaN(ms)) return null
  const diff = ms - now
  if (diff < 0) return 'overdue'
  if (diff <= DUE_SOON_MS) return 'dueSoon'
  return 'upcoming'
}
